/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */

import { useCallback, useRef, useEffect } from 'react';
import { useAuth as useClerkAuth } from '@clerk/clerk-react';
import { API_CONFIG } from '../config/api.config';

// Storage key for anonymous session ID
const ANONYMOUS_SESSION_KEY = 'guituitive_session_id';

// Buffer configuration
const BUFFER_FLUSH_INTERVAL = 5000; // 5 seconds
const MAX_BUFFER_SIZE = 100;

// Event types
export type ActivityEventType =
  | 'scale_selected'
  | 'chord_selected'
  | 'key_changed'
  | 'mode_toggled'
  | 'fretboard_note_clicked'
  | 'diatonic_chord_selected'
  | 'fingering_viewed'
  | 'jam_track_started'
  | 'jam_track_stopped'
  | 'chat_session_started'
  | 'chat_question_asked'
  | 'ear_training_completed'
  | 'fretboard_quiz_completed';

export interface MusicalContext {
  key: string;
  scaleOrChord: string;
  mode: 'scale' | 'chord';
  accidentalPreference?: 'sharps' | 'flats';
}

export interface InstrumentContext {
  strings: number;
  tuning: string;
  fretRangeStart?: number;
  fretRangeEnd?: number;
}

export interface ActivityEvent {
  eventType: ActivityEventType;
  musicalContext?: MusicalContext;
  instrumentContext?: InstrumentContext;
  payload?: Record<string, unknown>;
  clientTimestamp?: string;
}

interface BufferedEvent extends ActivityEvent {
  clientTimestamp: string;
}

interface BatchActivityLogRequest {
  events: BufferedEvent[];
  anonymousSessionId?: string;
  clientVersion?: string;
}

// Generate or retrieve anonymous session ID
const getOrCreateSessionId = (): string => {
  let sessionId = localStorage.getItem(ANONYMOUS_SESSION_KEY);
  if (!sessionId) {
    sessionId = `anon_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    localStorage.setItem(ANONYMOUS_SESSION_KEY, sessionId);
  }
  return sessionId;
};

// Client version from package.json (injected at build time if available)
const CLIENT_VERSION = process.env.REACT_APP_VERSION || '1.0.0';

/**
 * Hook for logging user activity events.
 * Uses fire-and-forget pattern with batching for minimal performance impact.
 */
export const useActivityLogger = () => {
  const { getToken, isSignedIn } = useClerkAuth();
  const eventBufferRef = useRef<BufferedEvent[]>([]);
  const flushTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Flush buffered events to the server
  const flushEvents = useCallback(async () => {
    const eventsToSend = [...eventBufferRef.current];
    if (eventsToSend.length === 0) {
      return;
    }

    // Clear buffer immediately
    eventBufferRef.current = [];

    const request: BatchActivityLogRequest = {
      events: eventsToSend,
      anonymousSessionId: isSignedIn ? undefined : getOrCreateSessionId(),
      clientVersion: CLIENT_VERSION,
    };

    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      // Add auth token if available
      if (isSignedIn) {
        const token = await getToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }

      // Fire and forget - use keepalive to ensure delivery even on page close
      fetch(`${API_CONFIG.API_BASE_URL}/api/activity/batch`, {
        method: 'POST',
        headers,
        body: JSON.stringify(request),
        keepalive: true,
      }).catch(() => {
        // Silently fail - we don't want to disrupt user experience
        // Could implement retry logic here if needed
      });
    } catch {
      // Silently fail
    }
  }, [getToken, isSignedIn]);

  // Schedule a flush
  const scheduleFlush = useCallback(() => {
    if (flushTimeoutRef.current) {
      return; // Already scheduled
    }
    flushTimeoutRef.current = setTimeout(() => {
      flushTimeoutRef.current = null;
      flushEvents();
    }, BUFFER_FLUSH_INTERVAL);
  }, [flushEvents]);

  // Log an activity event
  const logActivity = useCallback(
    (event: ActivityEvent) => {
      const bufferedEvent: BufferedEvent = {
        ...event,
        clientTimestamp: event.clientTimestamp || new Date().toISOString(),
      };

      eventBufferRef.current.push(bufferedEvent);

      // Immediate flush if buffer is full
      if (eventBufferRef.current.length >= MAX_BUFFER_SIZE) {
        if (flushTimeoutRef.current) {
          clearTimeout(flushTimeoutRef.current);
          flushTimeoutRef.current = null;
        }
        flushEvents();
      } else {
        scheduleFlush();
      }
    },
    [flushEvents, scheduleFlush]
  );

  // Convenience methods for common events
  const logScaleSelected = useCallback(
    (from: string, to: string, musicalContext?: MusicalContext, instrumentContext?: InstrumentContext) => {
      logActivity({
        eventType: 'scale_selected',
        musicalContext,
        instrumentContext,
        payload: { from, to },
      });
    },
    [logActivity]
  );

  const logChordSelected = useCallback(
    (from: string, to: string, musicalContext?: MusicalContext, instrumentContext?: InstrumentContext) => {
      logActivity({
        eventType: 'chord_selected',
        musicalContext,
        instrumentContext,
        payload: { from, to },
      });
    },
    [logActivity]
  );

  const logKeyChanged = useCallback(
    (from: string, to: string, musicalContext?: MusicalContext, instrumentContext?: InstrumentContext) => {
      logActivity({
        eventType: 'key_changed',
        musicalContext,
        instrumentContext,
        payload: { from, to },
      });
    },
    [logActivity]
  );

  const logModeToggled = useCallback(
    (from: 'scale' | 'chord', to: 'scale' | 'chord', musicalContext?: MusicalContext, instrumentContext?: InstrumentContext) => {
      logActivity({
        eventType: 'mode_toggled',
        musicalContext,
        instrumentContext,
        payload: { from, to },
      });
    },
    [logActivity]
  );

  const logFretboardNoteClicked = useCallback(
    (stringIndex: number, fret: number, note: string, musicalContext?: MusicalContext, instrumentContext?: InstrumentContext) => {
      logActivity({
        eventType: 'fretboard_note_clicked',
        musicalContext,
        instrumentContext,
        payload: { stringIndex, fret, note },
      });
    },
    [logActivity]
  );

  const logDiatonicChordSelected = useCallback(
    (degree: number, chord: string, romanNumeral: string, musicalContext?: MusicalContext, instrumentContext?: InstrumentContext) => {
      logActivity({
        eventType: 'diatonic_chord_selected',
        musicalContext,
        instrumentContext,
        payload: { degree, chord, romanNumeral },
      });
    },
    [logActivity]
  );

  const logFingeringViewed = useCallback(
    (chord: string, fingeringIndex: number, musicalContext?: MusicalContext, instrumentContext?: InstrumentContext) => {
      logActivity({
        eventType: 'fingering_viewed',
        musicalContext,
        instrumentContext,
        payload: { chord, fingeringIndex },
      });
    },
    [logActivity]
  );

  const logJamTrackStarted = useCallback(
    (trackId: string, style: string, key: string, bpm: number, musicalContext?: MusicalContext, instrumentContext?: InstrumentContext) => {
      logActivity({
        eventType: 'jam_track_started',
        musicalContext,
        instrumentContext,
        payload: { trackId, style, key, bpm },
      });
    },
    [logActivity]
  );

  const logJamTrackStopped = useCallback(
    (trackId: string, playDurationSeconds: number, musicalContext?: MusicalContext, instrumentContext?: InstrumentContext) => {
      logActivity({
        eventType: 'jam_track_stopped',
        musicalContext,
        instrumentContext,
        payload: { trackId, playDurationSeconds },
      });
    },
    [logActivity]
  );

  // Flush on unmount and page visibility change
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        flushEvents();
      }
    };

    const handleBeforeUnload = () => {
      flushEvents();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);

      // Flush remaining events on unmount
      if (flushTimeoutRef.current) {
        clearTimeout(flushTimeoutRef.current);
      }
      flushEvents();
    };
  }, [flushEvents]);

  return {
    logActivity,
    logScaleSelected,
    logChordSelected,
    logKeyChanged,
    logModeToggled,
    logFretboardNoteClicked,
    logDiatonicChordSelected,
    logFingeringViewed,
    logJamTrackStarted,
    logJamTrackStopped,
    flushEvents, // Expose for manual flush if needed
  };
};

export default useActivityLogger;
